import React from 'react';
import {
  Box, Grid, Typography, Chip, List, ListItem, ListItemText, Card, CardContent, Stack,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RouteIcon from '@mui/icons-material/Route';
import TimerIcon from '@mui/icons-material/Timer';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { useGrowthPlannerStore } from '../store/growthPlannerStore';
import { StatCard, ProgressBar, SectionHeader } from '../components/ui';
import {
  computeConsistencyScore,
  computeFocusScore,
  getTotalStudyHours,
  getUpcomingDeadlines,
  getWeekStart,
  ROUTE_STATUS,
} from '../utils/schema';

export default function Dashboard({ onNavigate }) {
  const routes = useGrowthPlannerStore((s) => s.routes);
  const learningResources = useGrowthPlannerStore((s) => s.learningResources);
  const focus = useGrowthPlannerStore((s) => s.focus);
  const skills = useGrowthPlannerStore((s) => s.skills);

  const activeRoutes = routes.filter((r) => r.status === 'active');
  const avgProgress = routes.length
    ? Math.round(routes.reduce((s, r) => s + (r.progress || 0), 0) / routes.length)
    : 0;

  const weekStart = getWeekStart();
  const weeklyHours = getTotalStudyHours(focus.studyLog, weekStart);
  const totalHours = getTotalStudyHours(focus.studyLog);
  const consistency = computeConsistencyScore(focus.studyLog);
  const focusScore = computeFocusScore(focus.weeklyGoals);
  const upcoming = getUpcomingDeadlines(routes, learningResources);

  const plannedWeekly = focus.weeklyGoals
    .filter((g) => g.weekStart === weekStart)
    .reduce((s, g) => s + (g.targetHours || 0), 0);

  return (
    <Box>
      <SectionHeader
        title="Dashboard"
        subtitle={new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      />

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Progreso general" value={`${avgProgress}%`} icon={<TrendingUpIcon />} color="#6366f1" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Rutas activas" value={activeRoutes.length} icon={<RouteIcon />} color="#8b5cf6" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Horas esta semana" value={`${weeklyHours}h`} subtitle={plannedWeekly ? `de ${plannedWeekly}h planificadas` : ''} icon={<TimerIcon />} color="#06b6d4" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Total estudiado" value={`${totalHours}h`} icon={<TimerIcon />} color="#14b8a6" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Racha" value={`${focus.streak?.current || 0} días`} subtitle={`Mejor: ${focus.streak?.longest || 0}`} icon={<LocalFireDepartmentIcon />} color="#f97316" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Focus Score" value={`${focusScore}%`} subtitle={`Consistencia: ${consistency}%`} color="#ec4899" />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Avance por ruta</Typography>
              {routes.length === 0 ? (
                <Typography color="text.secondary">Crea tu primera ruta profesional para comenzar.</Typography>
              ) : (
                routes.slice(0, 5).map((route) => (
                  <Box
                    key={route.id}
                    sx={{ mb: 2, cursor: 'pointer' }}
                    onClick={() => onNavigate?.('formation', route.id)}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={600}>{route.name}</Typography>
                      <Chip label={ROUTE_STATUS[route.status]?.label} size="small" color={ROUTE_STATUS[route.status]?.color} />
                    </Stack>
                    <ProgressBar value={route.progress || 0} label={route.targetRole || 'Sin cargo meta'} />
                  </Box>
                ))
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Progreso semanal</Typography>
              <ProgressBar
                value={plannedWeekly ? Math.round((weeklyHours / plannedWeekly) * 100) : 0}
                label={`${weeklyHours}h / ${plannedWeekly || '—'}h planificadas`}
                color="secondary"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Real vs planificado esta semana
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Objetivos próximos</Typography>
              {upcoming.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No hay deadlines próximos.</Typography>
              ) : (
                <List dense disablePadding>
                  {upcoming.map((item, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={item.title}
                        secondary={item.date}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      />
                      <Chip label={item.type} size="small" variant="outlined" />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Skills en desarrollo</Typography>
              {skills.length === 0 ? (
                <Typography color="text.secondary" variant="body2">Importa skills desde tu CV o agrégalas manualmente.</Typography>
              ) : (
                skills.slice(0, 6).map((skill) => (
                  <ProgressBar
                    key={skill.id}
                    value={skill.progress || 0}
                    label={`${skill.name} (${skill.currentLevel}→${skill.targetLevel})`}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
